import { useState } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFavoritesContext } from '@/contexts/FavoritesContext';
import { toastSuccess } from '@/lib/toast-utils';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  itemId: string;
  itemType: 'photo' | 'event';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
}

export function FavoriteButton({ 
  itemId, 
  itemType, 
  className,
  size = 'md',
  variant = 'ghost'
}: FavoriteButtonProps) {
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavoritesContext();
  const [isToggling, setIsToggling] = useState(false);
  
  const favorited = isFavorite(itemId, itemType);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isToggling) return;
    
    setIsToggling(true);
    
    try {
      if (favorited) {
        await removeFromFavorites(itemId, itemType);
        toastSuccess({
          title: 'Removido dos favoritos',
          description: 'Item removido dos seus favoritos com sucesso.'
        });
      } else {
        await addToFavorites(itemId, itemType);
        toastSuccess({
          title: 'Adicionado aos favoritos',
          description: 'Item adicionado aos seus favoritos com sucesso.'
        });
      }
    } catch (error) {
      // Erro já é tratado no hook
    } finally {
      setIsToggling(false);
    }
  };

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10'
  };

  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 20
  };

  return (
    <Button
      variant={variant}
      size="icon"
      onClick={handleToggleFavorite}
      disabled={isToggling}
      className={cn(
        sizeClasses[size],
        'transition-all duration-200 hover:scale-110',
        favorited && 'text-red-500 hover:text-red-600',
        !favorited && 'text-gray-400 hover:text-red-400',
        className
      )}
      title={favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
    >
      <Heart 
        size={iconSizes[size]} 
        className={cn(
          'transition-all duration-200',
          favorited && 'fill-current',
          isToggling && 'animate-pulse'
        )} 
      />
    </Button>
  );
}